import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ActionIcon, Box, Stack, Text } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { createColumnHelper } from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EditRolesAssignedToUser } from "../-EditRolesAssignedToUser";
import {
  isSparkRoleKey,
  SparkRoleFullDisplayName,
  type SparkRoleKey
} from "@/models/SparkRole";
import { getCommunityDetailsByIdQuery, useApi } from "@/api";
import LoadingPane from "@/components/LoadingPane";
import { DataTable } from "@/components/DataTable";
import {
  canSetCommunityAdmin,
  canSetOtherRoleAssignments
} from "@/security/logic";

export const Route = createFileRoute("/settings/community/$communityId/users")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Users"
    };
  }
});

interface UserRow {
  userId: number;
  entraObjectId: string;
  displayName: string;
  roles: string[];
  roleKeys: SparkRoleKey[];
}

function RouteComponent() {
  const { communityId } = Route.useParams();
  const { userClaim } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();
  const { data: community } = useQuery(
    getCommunityDetailsByIdQuery(secureAxios, Number(communityId))
  );

  const [modalOpened, setModalOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);

  const columnHelper = createColumnHelper<UserRow>();

  const userRows = useMemo(() => {
    if (!community) {
      return [];
    }

    // Group role assignments by user
    const userMap = new Map<
      number,
      {
        entraObjectId: string;
        displayName: string;
        roles: string[];
        roleKeys: SparkRoleKey[];
      }
    >();

    community.roleAssignments.forEach((assignment) => {
      const userId = assignment.userId;
      const displayName =
        assignment.displayName || assignment.userPrincipalName || "Unknown";
      const entraObjectId = assignment.entraObjectId || "";
      const roleKey = assignment.sparkRoleKey;

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          entraObjectId,
          displayName,
          roles: [],
          roleKeys: []
        });
      }

      if (roleKey && isSparkRoleKey(roleKey)) {
        const roleDisplayName = SparkRoleFullDisplayName[roleKey];
        userMap.get(userId)!.roles.push(roleDisplayName);
        userMap.get(userId)!.roleKeys.push(roleKey);
      }
    });

    // Convert map to array of rows, sorted by display name
    const rows: UserRow[] = Array.from(userMap.entries())
      .map(([userId, { entraObjectId, displayName, roles, roleKeys }]) => ({
        userId,
        entraObjectId,
        displayName,
        roles,
        roleKeys
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    return rows;
  }, [community]);

  if (!community) {
    return <LoadingPane />;
  }

  // Check if user can edit any role assignments
  const canEditCommunityAdmin = canSetCommunityAdmin(userClaim, community.id);
  const canEditOtherRoles = canSetOtherRoleAssignments(userClaim, community.id);
  const canEditAnyRole = canEditCommunityAdmin || canEditOtherRoles;

  // Build columns - only include Edit column if user can edit something
  const columns = [
    columnHelper.accessor("displayName", {
      header: "Name",
      meta: { style: { verticalAlign: "top" } }
    }),
    columnHelper.accessor("roles", {
      header: "Role(s)",
      cell: ({ getValue }) =>
        getValue().map((role) => <div key={role}>{role}</div>),
      meta: { style: { verticalAlign: "top" } }
    }),
    ...(canEditAnyRole
      ? [
          columnHelper.display({
            id: "actions",
            header: "Edit",
            meta: {
              style: { verticalAlign: "top", textAlign: "center", width: "1%" }
            },
            cell: ({ row }) => {
              return (
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    setSelectedUser(row.original);
                    setModalOpened(true);
                  }}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              );
            }
          })
        ]
      : [])
  ];

  return (
    <Stack gap={0}>
      <Text size="sm" c="dimmed" mb="md">
        A list of users assigned to this community and their assigned roles.
        (This is the same information as the Roles tab, displayed in a different
        way.)
        <br /> If a user is not listed, they do not have a role at this
        community. One can be added using the Roles tab.
      </Text>
      <Box w={600}>
        <DataTable data={userRows} columns={columns} />

        <EditRolesAssignedToUser
          opened={modalOpened}
          onClose={() => {
            setModalOpened(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            void queryClient.refetchQueries({
              queryKey: getCommunityDetailsByIdQuery(secureAxios, community.id)
                .queryKey
            });
            setSelectedUser(null);
          }}
          userId={selectedUser?.userId ?? 0}
          entraObjectId={selectedUser?.entraObjectId ?? ""}
          userDisplayName={selectedUser?.displayName ?? null}
          communityId={community.id}
          communityName={community.name}
          initialAssignedRoleKeys={selectedUser?.roleKeys ?? []}
          userClaim={userClaim}
        />
      </Box>
    </Stack>
  );
}
